'use client'

import { useEffect, useState } from 'react'

import { ArrowRight, X } from 'lucide-react'

import { SplitsLink } from '~/components/Link'
import { Button } from '~/components/component-library'
import {
  featureCardClickProperties,
  FeatureCardTracking,
  FEATURE_DISCOVERY_EVENTS,
} from '~/components/feature-cards/tracking'
import { cn } from '~/utils/tailwind-utils'
import { sendTrackingData } from '~/utils/tracking'

const CLOSE_ANIMATION_MS = 300

// Dismissible sidebar promo card; the dismiss button is a sibling of the link so its hover doesn't trigger the card's.
export const FeatureCard = ({
  title,
  description,
  ctaText,
  ctaHref,
  onCtaClick,
  onDismiss,
  showNewLabel = false,
  tracking,
  imageUrl,
  darkImageUrl,
}: {
  title: string
  description: string
  ctaText: string
  ctaHref: string
  onCtaClick?: () => void
  onDismiss: () => void
  showNewLabel?: boolean
  tracking?: FeatureCardTracking
  imageUrl?: string | null
  darkImageUrl?: string | null
}) => {
  const [isClosing, setIsClosing] = useState(false)
  const hasImage = !!imageUrl

  const closeThen = (callback?: () => void) => {
    setIsClosing(true)
    setTimeout(() => callback?.(), CLOSE_ANIMATION_MS)
  }

  // Fire the impression once when the card mounts.
  // eslint-disable-next-line local/noUseEffect -- one-shot view event fired on paint; no derived-state equivalent
  useEffect(() => {
    if (tracking) {
      sendTrackingData(FEATURE_DISCOVERY_EVENTS.VIEWED_FEATURE_CARD, tracking)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div
      className={cn(
        'grid grid-cols-1 transition-[grid-template-rows,opacity,transform] duration-300 ease-out motion-reduce:transition-none',
        {
          'translate-y-2 scale-95 grid-rows-[0fr] opacity-0': isClosing,
          'grid-rows-[1fr]': !isClosing,
        },
      )}
    >
      <div
        className={cn('relative min-h-0 min-w-0', {
          'overflow-hidden': isClosing,
        })}
      >
        <SplitsLink
          path={ctaHref}
          target="tab"
          eventName={
            tracking ? FEATURE_DISCOVERY_EVENTS.CLICKED_FEATURE_CARD : undefined
          }
          eventProperties={
            tracking
              ? featureCardClickProperties(tracking, 'sidebarCard')
              : undefined
          }
          onClick={onCtaClick}
          className={cn(
            'overlay-surface group flex flex-col rounded-lg dark:bg-gray-800',
            {
              'gap-1.5 p-3 pb-2.5': !hasImage,
              'overflow-hidden': hasImage,
            },
          )}
        >
          {hasImage ? (
            <>
              <div className="relative">
                {/* Light image hides in dark mode only when a dark variant exists. */}
                <div
                  className={cn('aspect-[16/9] w-full bg-cover bg-center', {
                    'dark:hidden': darkImageUrl,
                  })}
                  style={{ backgroundImage: `url("${imageUrl}")` }}
                />
                {darkImageUrl && (
                  <div
                    className="hidden aspect-[16/9] w-full bg-cover bg-center dark:block"
                    style={{ backgroundImage: `url("${darkImageUrl}")` }}
                  />
                )}
              </div>
              <div className="flex flex-col gap-1.5 border-t border-gray-200 px-3 pb-2.5 pt-1.5 dark:border-gray-800">
                <FeatureCardTitle title={title} />
                <FeatureCardCopy description={description} ctaText={ctaText} />
              </div>
            </>
          ) : (
            <>
              {/* -my-1.5 shrinks this row to text height so gaps/padding stay even */}
              <div className="-my-1.5 flex h-7 items-center pr-6">
                {showNewLabel ? (
                  <span className="text-xs font-medium text-blue-500">New</span>
                ) : (
                  <FeatureCardTitle title={title} />
                )}
              </div>
              {showNewLabel && <FeatureCardTitle title={title} />}
              <FeatureCardCopy description={description} ctaText={ctaText} />
            </>
          )}
        </SplitsLink>
        {hasImage ? (
          // Blurred scrim so the white X stays legible over any artwork.
          <button
            type="button"
            aria-label="Dismiss"
            onClick={() => {
              if (tracking) {
                sendTrackingData(
                  FEATURE_DISCOVERY_EVENTS.DISMISSED_FEATURE_CARD,
                  tracking,
                )
              }
              closeThen(onDismiss)
            }}
            className="absolute right-1.5 top-1.5 flex size-6 items-center justify-center rounded-full bg-black/30 text-white backdrop-blur-md transition-colors hover:bg-black/50"
          >
            <X className="size-3" />
          </button>
        ) : (
          <Button
            aria-label="Dismiss"
            variant="tertiary"
            size="small"
            className="absolute right-1.5 top-1.5 rounded-full p-1.5"
            prefix={<X height={12} width={12} />}
            eventName={
              tracking
                ? FEATURE_DISCOVERY_EVENTS.DISMISSED_FEATURE_CARD
                : undefined
            }
            eventProperties={tracking}
            onClick={() => closeThen(onDismiss)}
          />
        )}
      </div>
    </div>
  )
}

const FeatureCardTitle = ({ title }: { title: string }) => (
  <span className="min-w-0 truncate text-sm font-medium text-primary">
    {title}
  </span>
)

const FeatureCardCopy = ({
  description,
  ctaText,
}: {
  description: string
  ctaText: string
}) => (
  <>
    <p className="text-xs font-normal text-secondary">{description}</p>
    <div className="flex items-center gap-1 text-xs font-medium transition-colors group-hover:text-blue-500">
      {ctaText}
      <ArrowRight height={12} width={12} strokeWidth={2.25} />
    </div>
  </>
)
